import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Calendar, ChevronDown, ChevronUp, CheckCircle2, AlertTriangle,
  Info, Clock,
} from "lucide-react";
import api from "@/lib/api";
import { friendlyError } from "@/lib/errors";

/**
 * VisaTrackingCard — case-workspace panel to enter priority date +
 * preference category. Once set, the case is tracked against the
 * monthly Visa Bulletin and appears on the Dashboard banner.
 */

const CATEGORIES = [
  { id: "F1",   label: "F1 — Unmarried adult children of U.S. citizens" },
  { id: "F2A",  label: "F2A — Spouses & minor children of LPRs" },
  { id: "F2B",  label: "F2B — Unmarried adult children of LPRs" },
  { id: "F3",   label: "F3 — Married children of U.S. citizens" },
  { id: "F4",   label: "F4 — Brothers & sisters of U.S. citizens" },
  { id: "EB1",  label: "EB-1 — Priority workers" },
  { id: "EB2",  label: "EB-2 — Advanced degree / NIW" },
  { id: "EB3",  label: "EB-3 — Skilled workers, professionals" },
  { id: "EB3O", label: "EB-3 Other Workers" },
  { id: "EB4",  label: "EB-4 — Special immigrants" },
  { id: "EB5",  label: "EB-5 — Investors" },
];
const COUNTRIES = [
  { id: "ALL", label: "All other countries" },
  { id: "CHINA", label: "China (mainland)" },
  { id: "INDIA", label: "India" },
  { id: "MEXICO", label: "Mexico" },
  { id: "PHILIPPINES", label: "Philippines" },
];

const SEVERITY_STYLES = {
  success:  { bg: "#F0FDF4", border: "#BBF7D0", text: "#166534", icon: CheckCircle2 },
  info:     { bg: "#EFF6FF", border: "#BFDBFE", text: "#1E40AF", icon: Info },
  warning:  { bg: "#FFFBEB", border: "#FED7AA", text: "#B45309", icon: AlertTriangle },
  attention:{ bg: "#FEF2F2", border: "#FECACA", text: "#B91C1C", icon: AlertTriangle },
};


export default function VisaTrackingCard({ caseId }) {
  const [status, setStatus] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ priority_date: "", category: "", country: "ALL" });

  const load = useCallback(() => {
    api.get(`/cases/${caseId}/visa-status`)
      .then((r) => {
        setStatus(r.data);
        setForm({
          priority_date: r.data.priority_date || "",
          category: r.data.category || "",
          country: r.data.country || "ALL",
        });
        // Auto-expand if untracked (nudge the user to configure)
        if (!r.data.tracked && !r.data.is_immediate_relative) setOpen(true);
      })
      .catch(() => setStatus(null));
  }, [caseId]);

  useEffect(() => { if (caseId) load(); }, [caseId, load]);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch(`/cases/${caseId}/visa-tracking`, {
        priority_date: form.priority_date || null,
        category: form.category || null,
        country: form.country || "ALL",
      });
      toast.success("Priority date saved. You'll get monthly Visa Bulletin updates.");
      load();
    } catch (e) {
      toast.error(friendlyError(e, "Could not save tracking."));
    } finally {
      setBusy(false);
    }
  };

  if (!status) return null;

  // Immediate relatives don't need tracking (no numerical cap)
  if (status.is_immediate_relative) {
    const sty = SEVERITY_STYLES.success;
    return (
      <div
        className="card-surface p-4 mb-6"
        style={{ background: sty.bg, borderColor: sty.border }}
        data-testid="visa-tracking-card"
      >
        <div className="flex items-start gap-3">
          <CheckCircle2 className="w-5 h-5 mt-0.5" style={{ color: sty.text }} />
          <div>
            <div className="font-display font-bold text-sm" style={{ color: sty.text }}>
              Immediate relative — no visa-number wait
            </div>
            <div className="text-xs mt-0.5" style={{ color: sty.text }}>
              {status.banner_message}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const sty = SEVERITY_STYLES[status.banner_severity] || SEVERITY_STYLES.info;

  return (
    <div className="card-surface mb-6" style={{ background: "#FFFFFF" }} data-testid="visa-tracking-card">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between p-4 text-left"
        data-testid="visa-tracking-toggle"
      >
        <div className="flex items-center gap-3">
          <div
            className="w-11 h-11 rounded-md flex items-center justify-center flex-shrink-0"
            style={{ background: sty.bg, color: sty.text, border: `1px solid ${sty.border}` }}
          >
            <Calendar className="w-5 h-5" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-display font-extrabold text-base" style={{ color: "var(--text-primary)" }}>
                Priority date tracking
              </span>
              {status.tracked ? (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: sty.bg, color: sty.text }}
                >
                  {status.fad_status === "current" ? "CURRENT" :
                   status.fad_status === "unavailable" ? "UNAVAILABLE" :
                   status.fad_status === "backlogged" ? `~${status.estimated_wait_months || "?"} mo behind` :
                   "Waiting for bulletin"}
                </span>
              ) : (
                <span
                  className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold"
                  style={{ background: "#FEF3C7", color: "#78350F" }}
                >
                  Not tracked
                </span>
              )}
            </div>
            <div className="text-xs mt-0.5" style={{ color: "var(--text-secondary)" }}>
              Compare against the monthly Visa Bulletin (State Dept.)
            </div>
          </div>
        </div>
        {open ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
      </button>

      {open && (
        <div className="px-4 pb-4 border-t" style={{ borderColor: "var(--border-stone)" }}>
          {/* Status banner */}
          {status.banner_message && (
            <div
              className="mt-4 p-3 rounded-md border"
              style={{ background: sty.bg, borderColor: sty.border }}
            >
              <div className="flex items-start gap-2">
                <sty.icon className="w-4 h-4 mt-0.5 flex-shrink-0" style={{ color: sty.text }} />
                <div className="text-sm font-semibold" style={{ color: sty.text }}>
                  {status.banner_message}
                </div>
              </div>
              {status.bulletin_month && (
                <div className="text-[11px] mt-1 pl-6" style={{ color: sty.text, opacity: 0.8 }}>
                  Based on the {status.bulletin_month} Visa Bulletin
                </div>
              )}
            </div>
          )}

          {/* Input form */}
          <div className="mt-4 space-y-3">
            <div>
              <label className="field-label">Priority date</label>
              <input
                type="date"
                className="field-input"
                value={form.priority_date}
                onChange={(e) => setForm((f) => ({ ...f, priority_date: e.target.value }))}
                data-testid="visa-tracking-priority-date"
              />
              <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
                From your I-130 or I-140 receipt notice. Also known as the "priority date".
              </p>
            </div>
            <div>
              <label className="field-label">Preference category</label>
              <select
                className="field-input"
                value={form.category}
                onChange={(e) => setForm((f) => ({ ...f, category: e.target.value }))}
                data-testid="visa-tracking-category"
              >
                <option value="">Select category…</option>
                {CATEGORIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Country of chargeability</label>
              <select
                className="field-input"
                value={form.country}
                onChange={(e) => setForm((f) => ({ ...f, country: e.target.value }))}
                data-testid="visa-tracking-country"
              >
                {COUNTRIES.map((c) => (
                  <option key={c.id} value={c.id}>{c.label}</option>
                ))}
              </select>
              <p className="text-[11px] mt-1" style={{ color: "var(--text-secondary)" }}>
                Usually the beneficiary's country of birth. Some cases can "cross-charge"
                to a spouse's country if that country has a shorter wait — talk to your
                attorney about eligibility.
              </p>
            </div>
            <button
              type="button"
              onClick={save}
              disabled={busy}
              className="btn-primary text-sm"
              data-testid="visa-tracking-save"
            >
              {busy ? "Saving…" : "Save & track"}
            </button>
          </div>

          {/* Extra details when tracked */}
          {status.tracked && status.fad_cutoff && (
            <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
              <div>
                <div style={{ color: "var(--text-secondary)" }}>Your priority date</div>
                <div className="font-bold" style={{ color: "var(--text-primary)" }}>{status.priority_date}</div>
              </div>
              <div>
                <div style={{ color: "var(--text-secondary)" }}>This month's cutoff</div>
                <div className="font-bold" style={{ color: "var(--text-primary)" }}>{status.fad_cutoff}</div>
              </div>
              {status.dff_cutoff && (
                <div>
                  <div style={{ color: "var(--text-secondary)" }}>Dates-for-filing cutoff</div>
                  <div className="font-bold" style={{ color: "var(--text-primary)" }}>{status.dff_cutoff}</div>
                </div>
              )}
              {status.estimated_wait_months && (
                <div>
                  <div style={{ color: "var(--text-secondary)" }}>Estimated wait</div>
                  <div className="font-bold" style={{ color: "var(--text-primary)" }}>~{status.estimated_wait_months} months</div>
                </div>
              )}
            </div>
          )}

          <p className="text-[10.5px] italic mt-4" style={{ color: "var(--text-secondary)" }}>
            You'll receive a monthly email around the 15th when the next Visa Bulletin
            is published. Petitioner, beneficiary (if on file), and any linked attorney
            all receive the update.
          </p>
        </div>
      )}
    </div>
  );
}
